import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Globe, Radio, Cpu, Zap, Shield, CheckCircle2, RefreshCw, Layers, Signal } from 'lucide-react';
import { MathService } from '../services/mathService';

export const GlobalRecognition: React.FC = () => {
  const [isSyncing, setIsSyncing] = useState(false);
  const [activeLayer, setActiveLayer] = useState<number | null>(null);
  const [result, setResult] = useState<any>(null);
  const [completedLayers, setCompletedLayers] = useState<number[]>([]);

  const stats = MathService.globalRecognition();

  const handleSync = async () => {
    setIsSyncing(true);
    setResult(null);
    setCompletedLayers([]);
    
    for (let i = 0; i < stats.layers.length; i++) {
        setActiveLayer(i);
        await new Promise(r => setTimeout(r, 700));
        setCompletedLayers(prev => [...prev, i]);
    }
    
    setActiveLayer(null);
    setResult(stats);
    setIsSyncing(false);
  };

  return (
    <div className="glass-panel p-10 border-white/5 relative overflow-hidden bg-black/60 group">
      <div className="flex flex-col lg:flex-row gap-16 relative z-10">
        <div className="flex-1">
          <div className="mb-10">
            <h2 className="text-4xl font-display font-bold text-white mb-2 tracking-tighter">Global <span className="text-lattice-cyan italic">Recognition</span></h2>
            <p className="text-xs font-mono text-white/40 uppercase tracking-[0.3em] leading-relaxed max-w-xl">
              Broadcasting the Sovereign Value. Slaving real-world value to the Absolute Anchor via the 11 Billion node collective.
            </p>
          </div>

          <div className="space-y-8">
            <div className="space-y-4">
               {stats.layers.map((layer, index) => {
                 const isDone = completedLayers.includes(index);
                 const isActive = activeLayer === index;
                 
                 return (
                   <motion.div 
                     key={index}
                     className={`p-6 rounded-2xl border transition-all duration-500 ${
                       isActive ? 'border-lattice-cyan bg-lattice-cyan/5 shadow-[0_0_20px_rgba(45,212,191,0.1)]' : 
                       isDone ? 'border-white/20 bg-white/5' : 'border-white/5 opacity-30 shadow-none'
                     }`}
                   >
                      <div className="flex items-center justify-between">
                         <div className="flex items-center gap-4">
                            <Layers className={`w-5 h-5 ${isActive || isDone ? 'text-lattice-cyan' : 'text-white/10'}`} />
                            <span className={`text-[10px] font-mono font-bold uppercase tracking-widest ${isActive || isDone ? 'text-white' : 'text-white/20'}`}>
                              Layer 0{index + 1}: Metadata Sync
                            </span>
                         </div>
                         {isDone ? <CheckCircle2 className="w-4 h-4 text-lattice-cyan" /> : isActive ? <RefreshCw className="w-4 h-4 text-lattice-cyan animate-spin" /> : <Shield className="w-4 h-4 text-white/5" />}
                      </div>
                      {(isActive || isDone) && (
                        <p className="mt-3 text-[9px] font-mono text-lattice-cyan/60 uppercase ml-9">
                          {layer}
                        </p>
                      )}
                   </motion.div>
                 );
               })}
            </div>

            <button 
              onClick={handleSync}
              disabled={isSyncing}
              className="w-full py-5 bg-white text-black font-display font-bold text-sm tracking-[0.3em] rounded-2xl hover:bg-lattice-cyan transition-all flex items-center justify-center gap-4 disabled:opacity-50"
            >
              {isSyncing ? <Signal className="w-5 h-5 animate-pulse" /> : <Globe className="w-5 h-5" />}
              {isSyncing ? "BROADCASTING PULSE..." : "INITIATE RECOGNITION PULSE"}
            </button>
          </div>
        </div>

        <div className="w-full lg:w-[450px]">
           <AnimatePresence mode="wait">
              {result ? (
                <motion.div 
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="h-full bg-lattice-cyan p-10 rounded-[2.5rem] text-black flex flex-col justify-between relative overflow-hidden"
                >
                   <div className="relative z-10">
                      <div className="flex items-center gap-3 mb-10">
                        <Zap className="w-6 h-6 text-black" />
                        <span className="text-xs font-mono font-bold uppercase tracking-[0.2em]">Recognition Active</span>
                      </div>

                      <div className="space-y-6">
                         <div className="bg-black/5 p-6 rounded-2xl border border-black/5">
                            <span className="text-[8px] font-mono uppercase font-bold text-black/60 block mb-1">Resonance Root</span>
                            <p className="text-xl font-display font-bold tracking-tight">{result.resonance}</p>
                         </div>
                         <div className="bg-black/5 p-6 rounded-2xl border border-black/5">
                            <span className="text-[8px] font-mono uppercase font-bold text-black/60 block mb-1">Node Collective</span>
                            <p className="text-3xl font-display font-bold tracking-tighter">11,000,000,000</p>
                         </div>
                      </div>

                      <div className="mt-10 p-8 border-2 border-black/10 rounded-3xl bg-black/5">
                         <p className="text-[11px] font-mono font-bold leading-relaxed italic text-black/90 tracking-tight">
                           "{result.result}"
                         </p>
                      </div>
                   </div>

                   <div className="mt-12 flex items-center justify-between border-t border-black/10 pt-8 relative z-10">
                      <div className="flex flex-col">
                        <span className="text-[8px] font-mono uppercase font-bold opacity-60">Status</span>
                        <span className="text-sm font-display font-bold tracking-widest uppercase">Verified Unity</span>
                      </div>
                      <Radio className="w-10 h-10 opacity-20" />
                   </div>

                   {/* Absolute Pattern bg */}
                   <div className="absolute top-0 right-0 p-4 opacity-5 bg-black rounded-bl-3xl">
                      <Globe className="w-20 h-20" />
                   </div>
                </motion.div>
              ) : (
                <div className="h-full border-2 border-dashed border-white/10 rounded-[2.5rem] flex flex-col items-center justify-center p-12 text-center gap-10">
                   <div className="relative">
                      <Globe className="w-20 h-20 text-white/5" />
                      {isSyncing && (
                         <motion.div 
                           animate={{ scale: [1, 2], opacity: [0.2, 0] }}
                           transition={{ duration: 1, repeat: Infinity }}
                           className="absolute inset-0 bg-lattice-cyan rounded-full filter blur-xl"
                         />
                      )}
                   </div>
                   <div className="space-y-4">
                      <p className="text-[10px] font-mono text-white/20 uppercase tracking-[0.5em] leading-relaxed">
                        {isSyncing ? "Mapping 11B Nodes..." : "Awaiting Recognition Broadcast"}
                      </p>
                   </div>
                </div>
              )}
           </AnimatePresence>
        </div>
      </div>

      <div className="mt-12 pt-10 border-t border-white/5 grid grid-cols-2 lg:grid-cols-4 gap-8">
         <div className="space-y-2">
            <span className="text-[8px] font-mono text-white/20 uppercase tracking-widest block">Anchor State</span>
            <p className="text-sm font-mono text-lattice-cyan/80 font-bold">14350 BP Zero</p>
         </div>
         <div className="space-y-2">
            <span className="text-[8px] font-mono text-white/20 uppercase tracking-widest block">Logic Constant</span>
            <p className="text-sm font-mono text-lattice-cyan/80 font-bold">1 x 1 = 2</p>
         </div>
         <div className="space-y-2">
            <span className="text-[8px] font-mono text-white/20 uppercase tracking-widest block">Node Density</span>
            <p className="text-sm font-mono text-lattice-cyan/80 font-bold">Absolute Coverage</p>
         </div>
         <div className="space-y-2">
            <span className="text-[8px] font-mono text-white/20 uppercase tracking-widest block">Recognition Type</span>
            <p className="text-sm font-mono text-lattice-cyan/80 font-bold">Real-World Slave</p>
         </div>
      </div>
    </div>
  );
};
